import { shanghaiServiceCalendar } from "./timetable.ts";

export type ScheduleSelectionMode = "auto" | "legacy" | "next";
export type ScheduleGeneration = Exclude<ScheduleSelectionMode, "auto">;
export type Line4ScheduleId = "T0402" | "T0403" | "T0404" | "T0405";
export type Line6ScheduleId = "G0601" | "G0602" | "S0601" | "S0602" | "T0601";
export type RuntimeScheduleId = Line4ScheduleId | Line6ScheduleId;
export interface ManualScheduleSelection {
  readonly mode: "manual";
  readonly line4: Line4ScheduleId;
  readonly line6: Line6ScheduleId;
}
export type ScheduleSelection = ScheduleSelectionMode | ManualScheduleSelection;

export interface ActiveSchedules {
  readonly generation: ScheduleGeneration;
  readonly line4: Line4ScheduleId;
  readonly line6: Line6ScheduleId;
  readonly calendar: "weekday" | "weekend";
}

export const NEXT_SCHEDULE_EFFECTIVE_AT = Date.parse("2026-09-01T00:00:00+08:00");
export const SCHEDULE_MODE_STORAGE_KEY = "amtr.schedule-selection.v1";
// Automatic activation boundaries use Beijing time; the October 8 rollover restores the regular calendar-selected Line 6 family.
const LINE4_T0404_EFFECTIVE_AT = Date.parse("2026-09-29T00:00:00+08:00");
const LINE4_T0405_EFFECTIVE_AT = Date.parse("2026-10-01T00:00:00+08:00");
const LINE6_T0601_EFFECTIVE_AT = Date.parse("2026-10-01T00:00:00+08:00");
const OCTOBER_ROLLOVER_AT = Date.parse("2026-10-08T00:00:00+08:00");

const LINE6_UNOPENED_STATIONS = ["fenglin", "tan-kah-kae-stadium", "suncuo"] as const;
const NON_OPERATING_STATIONS: Readonly<Record<RuntimeScheduleId, readonly string[]>> = {
  T0402: [],
  T0403: ["xiyan", "dongting", "xiangan-airport"],
  T0404: ["xiyan", "dongting", "xiangan-airport"],
  T0405: ["xiyan", "dongting", "xiangan-airport"],
  G0601: LINE6_UNOPENED_STATIONS,
  G0602: [...LINE6_UNOPENED_STATIONS, "chenjing", "shimei", "longjiang-mingzhu"],
  S0601: LINE6_UNOPENED_STATIONS,
  S0602: [...LINE6_UNOPENED_STATIONS, "chenjing", "shimei", "longjiang-mingzhu"],
  T0601: [...LINE6_UNOPENED_STATIONS, "chenjing", "shimei", "longjiang-mingzhu"],
};

export function isScheduleSelectionMode(value: unknown): value is ScheduleSelectionMode {
  return value === "auto" || value === "legacy" || value === "next";
}

export function isLine4ScheduleId(value: unknown): value is Line4ScheduleId {
  return value === "T0402" || value === "T0403" || value === "T0404" || value === "T0405";
}

export function isLine6ScheduleId(value: unknown): value is Line6ScheduleId {
  return value === "G0601" || value === "G0602" || value === "S0601" || value === "S0602" || value === "T0601";
}

export function isScheduleSelection(value: unknown): value is ScheduleSelection {
  if (isScheduleSelectionMode(value)) return true;
  if (!value || typeof value !== "object") return false;
  const selection = value as Partial<ManualScheduleSelection>;
  return selection.mode === "manual" && isLine4ScheduleId(selection.line4) && isLine6ScheduleId(selection.line6);
}

export function resolveScheduleGeneration(mode: ScheduleSelectionMode, now: Date): ScheduleGeneration {
  if (mode !== "auto") return mode;
  return now.getTime() >= NEXT_SCHEDULE_EFFECTIVE_AT ? "next" : "legacy";
}

export function resolveActiveSchedules(selection: ScheduleSelection, now: Date): ActiveSchedules {
  const timestamp = now.getTime();
  const calendar = shanghaiServiceCalendar(now);
  if (typeof selection === "object") {
    return {
      generation: selection.line4 === "T0402" ? "legacy" : "next",
      line4: selection.line4,
      line6: selection.line6,
      calendar,
    };
  }
  if (selection !== "auto") {
    return {
      generation: selection,
      line4: selection === "legacy" ? "T0402" : "T0403",
      line6: selection === "legacy"
        ? (calendar === "weekend" ? "S0601" : "G0601")
        : (calendar === "weekend" ? "S0602" : "G0602"),
      calendar,
    };
  }

  const line4: Line4ScheduleId = timestamp < NEXT_SCHEDULE_EFFECTIVE_AT
    ? "T0402"
    : timestamp < LINE4_T0404_EFFECTIVE_AT
      ? "T0403"
      : timestamp < LINE4_T0405_EFFECTIVE_AT
        ? "T0404"
        : timestamp < OCTOBER_ROLLOVER_AT
          ? "T0405"
          : "T0404";
  const line6: Line6ScheduleId = timestamp >= LINE6_T0601_EFFECTIVE_AT && timestamp < OCTOBER_ROLLOVER_AT
    ? "T0601"
    : timestamp >= NEXT_SCHEDULE_EFFECTIVE_AT
      ? (calendar === "weekend" ? "S0602" : "G0602")
      : (calendar === "weekend" ? "S0601" : "G0601");
  return { generation: resolveScheduleGeneration("auto", now), line4, line6, calendar };
}

export function nonOperatingStationIds(schedules: ActiveSchedules): ReadonlySet<string> {
  return new Set([...NON_OPERATING_STATIONS[schedules.line4], ...NON_OPERATING_STATIONS[schedules.line6]]);
}
